import React, { useEffect, useState } from "react";
import api from "../../api";
import { useParams, useNavigate } from "react-router-dom";

/* ================= MATH FORMAT FUNCTION ================= */
function formatMath(text) {
  if (!text) return "";

  const superscriptMap = {
    "0": "⁰",
    "1": "¹",
    "2": "²",
    "3": "³",
    "4": "⁴",
    "5": "⁵",
    "6": "⁶",
    "7": "⁷",
    "8": "⁸",
    "9": "⁹",
    "-": "⁻",
  };

  return text.replace(/\^(-?\d+)/g, (match, power) => {
    return power
      .split("")
      .map((char) => superscriptMap[char] || char)
      .join("");
  });
}

export default function StudentTakePractice() {
  const { setId } = useParams();
  const nav = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [attemptId, setAttemptId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("token");

  const authConfig = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  /* ================= LOAD QUIZ ================= */
  useEffect(() => {
    if (!token) {
      alert("Session expired. Please login again.");
      nav("/login");
      return;
    }

    api
      .get(`/quiz/student/start_quiz/${setId}`, authConfig)
      .then((res) => {
        setQuestions(res.data.questions || []);
        setAttemptId(res.data.attempt_id);
        setTimeLeft(res.data.duration_seconds || 0);
      })
      .catch((err) => {
        console.error(err);

        if (err.response?.status === 401) {
          alert("Session expired. Please login again.");
          localStorage.removeItem("token");
          nav("/login");
        } else {
          alert("❌ Failed to start practice");
          nav("/student/practice-list");
        }
      })
      .finally(() => setLoading(false));
  }, [setId]);

  /* ================= TIMER ================= */
  useEffect(() => {
    if (!timeLeft) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  /* ================= SELECT ANSWER ================= */
  const selectAnswer = (qid, option) => {
    setAnswers((prev) => ({
      ...prev,
      [qid]: option,
    }));
  };

  /* ================= SAVE ================= */
  const saveAnswers = async () => {
    if (!attemptId) return;

    await api.post(
      `/quiz/student/save_quiz/${attemptId}`,
      { answers },
      authConfig
    );
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async () => {
    if (!attemptId) return;

    try {
      await saveAnswers();

      const res = await api.post(
        `/quiz/student/submit_quiz/${attemptId}`,
        {},
        authConfig
      );

      nav("/student/practice/result", {
        state: { attemptId: res.data.attempt_id },
      });
    } catch (err) {
      console.error(err);

      if (err.response?.status === 401) {
        alert("Session expired. Please login again.");
        localStorage.removeItem("token");
        nav("/login");
      } else {
        alert("❌ Failed to submit quiz");
      }
    }
  };

  /* ================= IMAGE HELPER ================= */
  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;

    const apiBase = api.defaults.baseURL;
    const root = apiBase.replace(/\/api\/?$/, "");
    return `${root}${path.startsWith("/") ? path : "/" + path}`;
  };

  if (loading) {
    return <p style={{ padding: 40 }}>⏳ Loading...</p>;
  }

  return (
    <div style={pageStyle}>
      <div style={overlayStyle}>
        <div style={contentWrapper}>
          {/* HEADER */}
          <div style={headerRow}>
            <img src="/logo.png" alt="EduAt10" style={logoStyle} />
            <button
              onClick={() => nav("/student/study")}
              style={backBtnStyle}
            >
              ← Back
            </button>
          </div>

          <h2>🧠 Practice Test</h2>

          {/* TIMER */}
          <div style={timerBox}>
            ⏱ Time Left:{" "}
            {Math.floor(timeLeft / 60)}:
            {String(timeLeft % 60).padStart(2, "0")}
          </div>

          {questions.map((q, index) => (
            <div key={q.id} style={cardStyle}>
              <p style={{ fontWeight: 600 }}>
                Q{index + 1}.
              </p>

              {/* ✅ APPLY FORMAT HERE */}
              {q.text && (
                <p style={{ marginBottom: 10 }}>
                  {formatMath(q.text)}
                </p>
              )}

              {q.question_image && (
                <img
                  src={getImageUrl(q.question_image)}
                  alt="Question"
                  style={questionImgStyle}
                />
              )}

              {["A", "B", "C", "D"].map((opt) => {
                const optionImage = q[`option_${opt.toLowerCase()}_image`];
                const optionText = q[`option_${opt.toLowerCase()}`];

                return (
                  <label key={opt} style={optionStyle}>
                    <input
                      type="radio"
                      name={`q-${q.id}`}
                      checked={answers[q.id] === opt}
                      onChange={() => selectAnswer(q.id, opt)}
                      style={{ marginRight: 8 }}
                    />

                    <strong>{opt}.</strong>{" "}

                    {optionImage ? (
                      <img
                        src={getImageUrl(optionImage)}
                        alt={opt}
                        style={optionImgStyle}
                      />
                    ) : (
                      formatMath(optionText)
                    )}
                  </label>
                );
              })}
            </div>
          ))}

          <button onClick={handleSubmit} style={submitBtn}>
            🚀 Submit Practice
          </button>
        </div>
      </div>
    </div>
  );
}

/* ================= STYLES ================= */
const pageStyle = {
  minHeight: "100vh",
  backgroundImage: "url('/eduat10-bg.png')",
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
};

const overlayStyle = {
  minHeight: "100vh",
  backgroundColor: "rgba(0,0,0,0.25)",
  padding: 20,
};

const contentWrapper = {
  background: "rgba(255,255,255,0.95)",
  borderRadius: 16,
  padding: 30,
  maxWidth: 900,
  margin: "0 auto",
};

const logoStyle = { width: 120 };

const headerRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 20,
};

const backBtnStyle = {
  padding: "8px 14px",
  background: "#02884e",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
};

const timerBox = {
  background: "#fff3cd",
  padding: 10,
  borderRadius: 8,
  marginBottom: 20,
  fontWeight: 600,
  position: "fixed",
  top: "10px",
  left: "40%",
};

const cardStyle = {
  marginBottom: 25,
  padding: 20,
  border: "1px solid #ddd",
  borderRadius: 12,
  background: "#ffffff",
};

const optionStyle = {
  display: "block",
  marginBottom: 10,
  cursor: "pointer",
};

const questionImgStyle = {
  maxWidth: "100%",
  marginBottom: 10,
  borderRadius: 8,
};

const optionImgStyle = {
  maxWidth: 150,
  display: "block",
  marginTop: 6,
  borderRadius: 6,
};

const submitBtn = {
  padding: "12px 22px",
  background: "#16a34a",
  color: "#fff",
  border: "none",
  borderRadius: 10,
  cursor: "pointer",
  fontWeight: 600,
};